// Ad copy generator — three tabs (LinkedIn, Indeed, plain text) with a
// one-click copy button on each. Calls Claude via /api/jobs/{id}/ad-copy.

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, RefreshCw, Sparkles, Check, History, ChevronRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TABS = [
  { key: "linkedin",  label: "LinkedIn"  },
  { key: "indeed",    label: "Indeed"    },
  { key: "plaintext", label: "Plain text" },
];

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString(undefined,
  { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export function AdCopyDialog({ open, onOpenChange, posting }) {
  const [loading, setLoading] = useState(false);
  const [copy,    setCopy]    = useState({ linkedin: "", indeed: "", plaintext: "" });
  const [copied,  setCopied]  = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = async () => {
    if (!posting) return;
    try {
      const { data } = await api.get(`/jobs/${posting.posting_id}/ad-copy-history`);
      setHistory(data.items || []);
    } catch { setHistory([]); }
  };
  useEffect(() => { if (open) loadHistory(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, posting?.posting_id]);

  const generate = async () => {
    if (!posting) return;
    try {
      setLoading(true);
      const { data } = await api.post(`/jobs/${posting.posting_id}/ad-copy`);
      setCopy({
        linkedin:  data.linkedin  || "",
        indeed:    data.indeed    || "",
        plaintext: data.plaintext || "",
      });
      await loadHistory();
      toast.success("Ad copy generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed");
    } finally { setLoading(false); }
  };

  const loadFromHistory = (h) => {
    setCopy({
      linkedin:  h.linkedin  || "",
      indeed:    h.indeed    || "",
      plaintext: h.plaintext || "",
    });
    setShowHistory(false);
    toast.success("Loaded draft from history");
  };

  const doCopy = async (key) => {
    const text = copy[key] || "";
    if (!text) { toast.error("Nothing to copy"); return; }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(""), 1600);
    } catch {
      toast.error("Copy blocked by the browser — select and Cmd/Ctrl+C manually");
    }
  };

  const empty = !copy.linkedin && !copy.indeed && !copy.plaintext;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="adcopy-dialog">
        <DialogHeader>
          <DialogTitle>Ad copy — {posting?.title}</DialogTitle>
          <DialogDescription>
            Claude drafts three variants. Copy the one that fits and tweak in-place
            before pasting. We keep the last 10 drafts so you can compare tones.
          </DialogDescription>
        </DialogHeader>

        {empty && !loading && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-8 text-center"
               data-testid="adcopy-empty">
            <Sparkles className="mx-auto h-8 w-8 text-slate-400" />
            <p className="mt-2 text-sm text-slate-600">Click Generate to draft LinkedIn, Indeed, and plain-text versions.</p>
            <Button onClick={generate} disabled={loading} data-testid="adcopy-generate-btn"
                    className="mt-4 gap-2">
              <Sparkles className="h-4 w-4" /> Generate ad copy
            </Button>
            {history.length > 0 && (
              <button onClick={() => setShowHistory(true)}
                      data-testid="adcopy-view-history-empty"
                      className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                <History className="h-3 w-3" /> Or view {history.length} past draft{history.length === 1 ? "" : "s"}
              </button>
            )}
          </div>
        )}

        {loading && (
          <div className="py-10 text-center">
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" />
            <p className="mt-3 text-xs text-slate-500">Drafting with Claude Sonnet 4.6…</p>
          </div>
        )}

        {!empty && !loading && (
          <>
            <Tabs defaultValue="linkedin">
              <TabsList className="grid grid-cols-3" data-testid="adcopy-tabs">
                {TABS.map((t) => (
                  <TabsTrigger key={t.key} value={t.key} data-testid={`adcopy-tab-${t.key}`}>
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {TABS.map((t) => (
                <TabsContent key={t.key} value={t.key} className="space-y-2">
                  <Textarea rows={12} value={copy[t.key] || ""}
                            onChange={(e) => setCopy((c) => ({ ...c, [t.key]: e.target.value }))}
                            data-testid={`adcopy-body-${t.key}`}
                            className="font-mono text-xs whitespace-pre-wrap" />
                  <div className="flex justify-end">
                    <Button onClick={() => doCopy(t.key)}
                            data-testid={`adcopy-copy-${t.key}`}
                            variant="outline" size="sm" className="gap-1.5">
                      {copied === t.key
                        ? <><Check className="h-3.5 w-3.5 text-emerald-600" /> Copied</>
                        : <><Copy className="h-3.5 w-3.5" /> Copy {t.label}</>}
                    </Button>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
            <div className="flex items-center justify-between">
              <button onClick={() => setShowHistory((v) => !v)}
                      data-testid="adcopy-toggle-history"
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                <History className="h-3.5 w-3.5" /> {showHistory ? "Hide" : `History (${history.length})`}
              </button>
              <Button variant="ghost" size="sm" onClick={generate} disabled={loading}
                      data-testid="adcopy-regenerate"
                      className="gap-1.5 text-xs">
                <RefreshCw className="h-3 w-3" /> Regenerate
              </Button>
            </div>
          </>
        )}

        {showHistory && (
          <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2 max-h-56 overflow-y-auto"
               data-testid="adcopy-history-list">
            {history.length === 0 ? (
              <div className="p-3 text-center text-xs text-slate-500">
                No history yet — hit Generate to add the first draft.
              </div>
            ) : (
              <ul className="divide-y divide-slate-200">
                {history.map((h) => (
                  <li key={h.ad_id}>
                    <button onClick={() => loadFromHistory(h)}
                            data-testid={`adcopy-history-${h.ad_id}`}
                            className="flex w-full items-center gap-2 py-2 px-2 text-left hover:bg-white rounded transition">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-slate-700">
                          {fmtWhen(h.created_at)} · {h.created_by_name || "—"}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {(h.linkedin || h.indeed || h.plaintext || "").slice(0, 120)}…
                        </div>
                      </div>
                      <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
